/**
 * Typed error thrown by the HTTP client for every non-2xx response (phase C.5).
 *
 * Until now `httpClient` threw a bare `Error` carrying only a message, so callers
 * could not branch on the HTTP status or the backend's machine-readable `code`.
 * The version/maintenance interceptor needs both: a 426 with
 * `app_version_unsupported` and a 423 maintenance block are distinguished purely
 * by `status` + `code`, never by the human `message` (which is localized by the
 * UI, not matched here).
 *
 * `ApiError extends Error`, so existing `catch`/`instanceof Error`/`.message`
 * consumers keep working unchanged — this only *adds* structured fields.
 */
export type ApiErrorInit = {
  /** HTTP status code of the failed response. */
  status: number;
  /** Backend machine-readable error code (flat payload `code`), or `null` when absent. */
  code: string | null;
  /** Human-readable message (already flattened from the payload); inherited via `Error`. */
  message: string;
  /** Backend `details` payload, when present. Shape is endpoint-specific. */
  details?: unknown;
  /**
   * DRF-style field validation errors lifted from the envelope's `errors` map
   * (`{ field: string[] }`), or `null` when the body is not a field map (e.g. a
   * 401 `{ detail, code }`). Lets forms surface per-field messages.
   */
  fieldErrors?: Record<string, string[]> | null;
  /**
   * The originating `Response`. Its body is already consumed by the client while
   * extracting `code`/`message`/`details`; only headers (e.g. `Retry-After`)
   * remain readable. Stored so the interceptor can inspect those headers.
   */
  response: Response;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: unknown;
  readonly fieldErrors: Record<string, string[]> | null;
  readonly response: Response;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.fieldErrors = init.fieldErrors ?? null;
    this.response = init.response;

    // Restore the prototype chain so `instanceof ApiError` survives transpilation
    // to ES5-style class emulation.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/** Type guard: narrows an unknown caught value to `ApiError`. */
export const isApiError = (value: unknown): value is ApiError => value instanceof ApiError;
